import { twMerge } from "tailwind-merge";
import { Link } from "react-router-dom";
import { Edit, ExternalLink } from "lucide-react";
import { UserWithProfile } from "@shared/features/auth/auth.types";
import { Avatar } from "@shared/ui/Avatar";
import { Type } from "@shared/ui/Type";
import { Button } from "@shared/ui/Button";
import { ProgramMeetingRoleBadge } from "../ProgramMeetingRoleBadge/ProgramMeetingRoleBadge";
import { MeetingRole } from "@platform/types/platform.types";

export type Props = {
  className?: string;
  participant?: UserWithProfile;
  participantRole?: MeetingRole;
  isWithBio?: boolean;
  isWithEditButton?: boolean;
  isWithWebsite?: boolean;
};

export function ProgramCircleParticipant({
  participant,
  className,
  participantRole,
  isWithBio = true,
  isWithEditButton = true,
  isWithWebsite = true,
}: Props) {
  const website = participant?.website?.includes("http")
    ? participant?.website
    : `https://${participant?.website}`;

  return (
    <div
      className={twMerge(
        "border-brand-800 border-b odd:border-r p-4 flex flex-row gap-4",
        className
      )}
    >
      <Avatar
        image={participant?.profile_photo}
        size={48}
        alt={`${participant?.first_name} ${participant?.last_name}`}
      />
      <div className="flex flex-col gap-2 flex-1">
        <div className="flex flex-row gap-4 items-center">
          <Type variant="h5">
            {participant?.first_name} {participant?.last_name}
          </Type>
          {participant?.pronouns && (
            <Type variant="caption">{participant?.pronouns}</Type>
          )}
        </div>
        {participantRole && <ProgramMeetingRoleBadge role={participantRole} />}
        {participant?.website && isWithWebsite && (
          <Button
            as="a"
            variant="link-inline"
            iconComponent={ExternalLink}
            target="_blank"
            rel="noopener noreferrer"
            href={website}
            title={`View ${participant?.first_name} ${participant?.last_name}'s website`}
            typeClassName="inline-block max-w-72 truncate"
            className="flex flex-row gap-1"
          >
            {website}
          </Button>
        )}
        {participant?.bio && isWithBio && (
          <Type variant="caption" className="line-clamp-16">
            {participant?.bio}
          </Type>
        )}
        {participant?.isUser && isWithEditButton && (
          <Button
            as={Link}
            variant="link-inline"
            iconComponent={Edit}
            className="self-end mt-4"
            to="/account"
          >
            Edit Your Profile
          </Button>
        )}
      </div>
    </div>
  );
}

export default ProgramCircleParticipant;
